import { useState, useEffect } from 'react';

const C = '#1b6ca8';
const DARK = '#0f4c75';

const BACKEND_BASE = window.location.port === '3000' ? 'http://localhost:8080' : '';

function pub(oid, path) {
  return `${BACKEND_BASE}/spring/hospital/api/public/${oid}${path}`;
}

const TIME_SLOTS = [
  '08:00', '08:30', '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00', '15:30',
  '16:00', '16:30', '17:00', '17:30', '18:00',
];

function inp(extra = {}) {
  return {
    style: {
      display: 'block', width: '100%', padding: '10px 14px',
      border: '1.5px solid #e8eaed', borderRadius: 8, fontSize: 14,
      outline: 'none', boxSizing: 'border-box', ...extra,
    },
  };
}

function Field({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#5f6368',
        textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 5 }}>{label}</label>
      {children}
    </div>
  );
}

function Err({ msg }) {
  if (!msg) return null;
  return (
    <div style={{ background: '#fce8e6', color: '#c5221f', padding: '10px 14px',
      borderRadius: 8, fontSize: 13, marginBottom: 14 }}>{msg}</div>
  );
}

export default function HospitalPublicPage({ ownersid }) {
  const [info, setInfo]                   = useState(null);
  const [step, setStep]                   = useState(1);
  const [specializations, setSpecs]       = useState([]);
  const [selectedSpec, setSelectedSpec]   = useState('');
  const [doctors, setDoctors]             = useState([]);
  const [selectedDoctor, setSelectedDoc]  = useState(null);
  const [apptDate, setApptDate]           = useState('');
  const [apptTime, setApptTime]           = useState('');
  const [bookedTimes, setBooked]          = useState([]);
  const [slotsLoading, setSlotsLoading]   = useState(false);
  const [form, setForm]                   = useState({
    name: '', phone: '', email: '', dob: '', gender: '', username: '', password: '', confirmPwd: '',
  });
  const [err, setErr]   = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [skipAppt, setSkipAppt] = useState(false);

  useEffect(() => {
    fetch(pub(ownersid, '/info')).then(r => r.json()).then(setInfo).catch(() => {});
    fetch(pub(ownersid, '/specializations')).then(r => r.json()).then(rows => {
      setSpecs(rows.map(r => r.specialization).filter(Boolean));
    }).catch(() => {});
  }, [ownersid]);

  useEffect(() => {
    if (step !== 2) return;
    const url = selectedSpec
      ? pub(ownersid, `/doctors?specialization=${encodeURIComponent(selectedSpec)}`)
      : pub(ownersid, '/doctors');
    fetch(url).then(r => r.json()).then(setDoctors).catch(() => {});
  }, [step, selectedSpec, ownersid]);

  useEffect(() => {
    if (!selectedDoctor || !apptDate) { setBooked([]); return; }
    setSlotsLoading(true);
    fetch(pub(ownersid, `/available-slots?doctorId=${selectedDoctor.id}&date=${apptDate}`))
      .then(r => r.json())
      .then(d => { setBooked(d.bookedTimes || []); setSlotsLoading(false); })
      .catch(() => setSlotsLoading(false));
  }, [selectedDoctor, apptDate, ownersid]);

  const today = new Date().toISOString().split('T')[0];

  const handleSubmit = async () => {
    const { name, phone, username, password, confirmPwd } = form;
    if (!name.trim()) { setErr('Full name is required'); return; }
    if (!phone.trim()) { setErr('Phone number is required'); return; }
    if (!username.trim()) { setErr('Username is required'); return; }
    if (password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (password !== confirmPwd) { setErr('Passwords do not match'); return; }
    if (!skipAppt && !selectedDoctor) { setErr('Please select a doctor or choose to skip appointment'); return; }
    if (!skipAppt && selectedDoctor && !apptDate) { setErr('Please select appointment date'); return; }

    setSaving(true); setErr('');
    try {
      const payload = {
        name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim(),
        dob: form.dob, gender: form.gender,
        username: form.username.trim(), password: form.password,
        doctorId: skipAppt ? null : (selectedDoctor?.id ?? null),
        apptDate: skipAppt ? '' : apptDate,
        apptTime: skipAppt ? '' : apptTime,
      };
      const res = await fetch(pub(ownersid, '/self-register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.error) { setErr(data.error); return; }
      setResult(data);
      setStep(5);
    } catch (e) {
      setErr('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  const hospitalName = info?.hospital_name || 'Hospital';
  const hospitalPhone = info?.phone || '';

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#0f4c75 0%,#0a3555 100%)',
      fontFamily: 'Inter,system-ui,sans-serif' }}>

      {/* Header */}
      <div style={{ background: 'rgba(0,0,0,0.25)', padding: '16px 24px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>🏥 {hospitalName}</div>
          {hospitalPhone && <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)' }}>📞 {hospitalPhone}</div>}
        </div>
        <a href={`${BACKEND_BASE}/spring/login`}
          style={{ fontSize: 13, color: '#7ec8e3', textDecoration: 'none', fontWeight: 600 }}>
          Existing patient? Sign in →
        </a>
      </div>

      {/* Progress bar */}
      {step < 5 && (
        <div style={{ background: 'rgba(0,0,0,0.15)', padding: '12px 24px' }}>
          <div style={{ display: 'flex', gap: 8, maxWidth: 600, margin: '0 auto' }}>
            {[
              { n: 1, label: 'Welcome' },
              { n: 2, label: 'Doctor' },
              { n: 3, label: 'Schedule' },
              { n: 4, label: 'Register' },
            ].map(({ n, label }) => (
              <div key={n} style={{ flex: 1, textAlign: 'center' }}>
                <div style={{
                  width: 28, height: 28, borderRadius: '50%', margin: '0 auto 4px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 13, fontWeight: 700,
                  background: step >= n ? '#4CCEE8' : 'rgba(255,255,255,0.2)',
                  color: step >= n ? '#0f4c75' : 'rgba(255,255,255,0.6)',
                }}>{n}</div>
                <div style={{ fontSize: 10, color: step >= n ? '#4CCEE8' : 'rgba(255,255,255,0.5)',
                  fontWeight: step === n ? 700 : 400 }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Content */}
      <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 16px 80px' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '36px 32px',
          width: '100%', maxWidth: step === 2 ? 800 : 480, boxShadow: '0 24px 60px rgba(0,0,0,0.4)' }}>

          {/* Step 1 — Welcome */}
          {step === 1 && (
            <div>
              <div style={{ fontSize: 40, textAlign: 'center', marginBottom: 12 }}>🏥</div>
              <h2 style={{ textAlign: 'center', color: DARK, marginBottom: 8 }}>{hospitalName}</h2>
              <p style={{ textAlign: 'center', color: '#5f6368', fontSize: 14, marginBottom: 28 }}>
                Book an appointment with our specialists and register as a patient in minutes.
              </p>
              <button onClick={() => { setSkipAppt(false); setStep(2); }}
                style={{ display: 'block', width: '100%', padding: 14, background: C, color: '#fff',
                  border: 'none', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer', marginBottom: 12 }}>
                Book Appointment + Register
              </button>
              <button onClick={() => { setSkipAppt(true); setStep(4); }}
                style={{ display: 'block', width: '100%', padding: 14, background: '#f8f9fa', color: DARK,
                  border: `1.5px solid #e8eaed`, borderRadius: 10, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                Register Only (No Appointment)
              </button>
              <div style={{ textAlign: 'center', marginTop: 24, fontSize: 13, color: '#9aa0a6' }}>
                Already have an account?{' '}
                <a href={`${BACKEND_BASE}/spring/login`}
                  style={{ color: C, textDecoration: 'none', fontWeight: 600 }}>Sign in here</a>
              </div>
            </div>
          )}

          {/* Step 2 — Choose Doctor */}
          {step === 2 && (
            <div>
              <h2 style={{ color: DARK, marginBottom: 6 }}>Choose a Doctor</h2>
              <p style={{ color: '#5f6368', fontSize: 14, marginBottom: 20 }}>
                Browse by specialization or select any available doctor.
              </p>

              {/* Specialization filter chips */}
              {specializations.length > 0 && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 }}>
                  <button onClick={() => setSelectedSpec('')}
                    style={{ padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      background: !selectedSpec ? C : '#f1f3f4', color: !selectedSpec ? '#fff' : '#5f6368',
                      border: !selectedSpec ? `1.5px solid ${C}` : '1.5px solid #e8eaed' }}>All</button>
                  {specializations.map(s => (
                    <button key={s} onClick={() => setSelectedSpec(s)}
                      style={{ padding: '6px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        background: selectedSpec === s ? C : '#f1f3f4', color: selectedSpec === s ? '#fff' : '#5f6368',
                        border: selectedSpec === s ? `1.5px solid ${C}` : '1.5px solid #e8eaed' }}>{s}</button>
                  ))}
                </div>
              )}

              {/* Doctor cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 12, marginBottom: 20 }}>
                {doctors.length === 0 && (
                  <div style={{ gridColumn: '1/-1', textAlign: 'center', color: '#9aa0a6', fontSize: 14, padding: 24 }}>
                    No doctors found
                  </div>
                )}
                {doctors.map(d => (
                  <div key={d.id} onClick={() => setSelectedDoc(d)}
                    style={{ padding: 16, borderRadius: 12, cursor: 'pointer', transition: 'all .15s',
                      border: selectedDoctor?.id === d.id ? `2px solid ${C}` : '2px solid #e8eaed',
                      background: selectedDoctor?.id === d.id ? '#e8f4fd' : '#fff' }}>
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: C,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 20, marginBottom: 8 }}>👨‍⚕️</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: DARK }}>Dr. {d.name}</div>
                    <div style={{ fontSize: 12, color: '#5f6368', marginTop: 2 }}>{d.specialization || '—'}</div>
                    {d.consult_fee > 0 && (
                      <div style={{ fontSize: 12, color: '#27ae60', fontWeight: 600, marginTop: 4 }}>
                        ₹{Number(d.consult_fee).toLocaleString()}
                      </div>
                    )}
                    {d.availability_days && (
                      <div style={{ fontSize: 11, color: '#9aa0a6', marginTop: 4 }}>📅 {d.availability_days}</div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', gap: 10 }}>
                <button onClick={() => setStep(1)}
                  style={{ flex: 1, padding: 12, background: '#f1f3f4', color: DARK, border: 'none',
                    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
                <button onClick={() => { if (!selectedDoctor) { setErr('Please select a doctor'); return; } setErr(''); setStep(3); }}
                  style={{ flex: 2, padding: 12, background: C, color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  Next: Schedule →
                </button>
              </div>
              <Err msg={err} />
            </div>
          )}

          {/* Step 3 — Date & Time */}
          {step === 3 && (
            <div>
              <h2 style={{ color: DARK, marginBottom: 6 }}>Schedule Appointment</h2>
              <p style={{ color: '#5f6368', fontSize: 14, marginBottom: 20 }}>
                With <strong>Dr. {selectedDoctor?.name}</strong> · {selectedDoctor?.specialization}
              </p>

              <Field label="Appointment Date">
                <input type="date" min={today} value={apptDate}
                  onChange={e => { setApptDate(e.target.value); setApptTime(''); }}
                  {...inp()} />
              </Field>

              {apptDate && (
                <Field label="Time Slot">
                  {slotsLoading ? (
                    <div style={{ color: '#5f6368', fontSize: 13, padding: '10px 0' }}>Loading slots…</div>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {TIME_SLOTS.map(t => {
                        const booked = bookedTimes.includes(t);
                        const selected = apptTime === t;
                        return (
                          <button key={t} disabled={booked} onClick={() => setApptTime(t)}
                            style={{ padding: '6px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600,
                              cursor: booked ? 'not-allowed' : 'pointer',
                              background: booked ? '#f1f3f4' : selected ? C : '#e8f4fd',
                              color: booked ? '#bbb' : selected ? '#fff' : C,
                              border: selected ? `1.5px solid ${C}` : '1.5px solid #e8eaed',
                              textDecoration: booked ? 'line-through' : 'none' }}>
                            {t}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </Field>
              )}

              <Err msg={err} />

              <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
                <button onClick={() => { setErr(''); setStep(2); }}
                  style={{ flex: 1, padding: 12, background: '#f1f3f4', color: DARK, border: 'none',
                    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
                <button onClick={() => {
                  if (!apptDate) { setErr('Please select a date'); return; }
                  setErr(''); setStep(4);
                }}
                  style={{ flex: 2, padding: 12, background: C, color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
                  Next: Your Details →
                </button>
              </div>
            </div>
          )}

          {/* Step 4 — Registration form */}
          {step === 4 && (
            <div>
              <h2 style={{ color: DARK, marginBottom: 6 }}>Your Details</h2>
              <p style={{ color: '#5f6368', fontSize: 14, marginBottom: 20 }}>
                {skipAppt ? 'Create your patient account.' : `Registering for appointment with Dr. ${selectedDoctor?.name} on ${apptDate}${apptTime ? ' at ' + apptTime : ''}.`}
              </p>

              <Err msg={err} />

              <Field label="Full Name *">
                <input type="text" value={form.name} placeholder="Enter your full name"
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))} {...inp()} />
              </Field>
              <Field label="Phone Number *">
                <input type="tel" value={form.phone} placeholder="10-digit mobile number"
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} {...inp()} />
              </Field>
              <Field label="Email">
                <input type="email" value={form.email} placeholder="Email address (optional)"
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))} {...inp()} />
              </Field>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <Field label="Date of Birth">
                  <input type="date" value={form.dob}
                    onChange={e => setForm(f => ({ ...f, dob: e.target.value }))} {...inp()} />
                </Field>
                <Field label="Gender">
                  <select value={form.gender} onChange={e => setForm(f => ({ ...f, gender: e.target.value }))}
                    style={{ display: 'block', width: '100%', padding: '10px 14px', border: '1.5px solid #e8eaed',
                      borderRadius: 8, fontSize: 14, outline: 'none', background: '#fff' }}>
                    <option value="">Select</option>
                    <option>Male</option>
                    <option>Female</option>
                    <option>Other</option>
                  </select>
                </Field>
              </div>

              <div style={{ borderTop: '1px solid #f1f3f4', margin: '16px 0', paddingTop: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: DARK, marginBottom: 12 }}>
                  Create Patient Portal Account
                </div>
                <Field label="Username *">
                  <input type="text" value={form.username} placeholder="Choose a username (e.g. phone number)"
                    onChange={e => setForm(f => ({ ...f, username: e.target.value }))} {...inp()} />
                </Field>
                <Field label="Password *">
                  <input type="password" value={form.password} placeholder="At least 6 characters"
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))} {...inp()} />
                </Field>
                <Field label="Confirm Password *">
                  <input type="password" value={form.confirmPwd} placeholder="Repeat password"
                    onChange={e => setForm(f => ({ ...f, confirmPwd: e.target.value }))} {...inp()} />
                </Field>
              </div>

              <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
                <button onClick={() => { setErr(''); setStep(skipAppt ? 1 : 3); }}
                  style={{ flex: 1, padding: 12, background: '#f1f3f4', color: DARK, border: 'none',
                    borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>← Back</button>
                <button onClick={handleSubmit} disabled={saving}
                  style={{ flex: 2, padding: 12, background: saving ? '#9aa0a6' : C, color: '#fff', border: 'none',
                    borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: saving ? 'default' : 'pointer' }}>
                  {saving ? 'Registering…' : '✓ Complete Registration'}
                </button>
              </div>
            </div>
          )}

          {/* Step 5 — Success */}
          {step === 5 && result && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, marginBottom: 12 }}>✅</div>
              <h2 style={{ color: '#27ae60', marginBottom: 8 }}>Registration Successful!</h2>
              <p style={{ color: '#5f6368', fontSize: 14, marginBottom: 24 }}>
                You've been registered as a patient at {hospitalName}.
              </p>
              <div style={{ background: '#e8f4fd', borderRadius: 12, padding: 20, marginBottom: 24, textAlign: 'left' }}>
                <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 4 }}>Patient Code</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: DARK, marginBottom: 16 }}>{result.patientCode}</div>
                {result.appointmentId && (
                  <>
                    <div style={{ fontSize: 13, color: '#5f6368', marginBottom: 4 }}>Appointment Booked</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#27ae60' }}>
                      Dr. {selectedDoctor?.name} — {apptDate}{apptTime ? ' at ' + apptTime : ''}
                    </div>
                  </>
                )}
              </div>
              <div style={{ fontSize: 14, color: '#5f6368', marginBottom: 20 }}>
                Use your username and password to access your health records via the Patient Portal.
              </div>
              <a href={`${BACKEND_BASE}/spring/login`}
                style={{ display: 'block', padding: 14, background: C, color: '#fff', borderRadius: 10,
                  fontSize: 15, fontWeight: 700, textDecoration: 'none', marginBottom: 12 }}>
                Sign In to Patient Portal →
              </a>
              <button onClick={() => {
                setStep(1); setSelectedDoc(null); setApptDate(''); setApptTime('');
                setForm({ name: '', phone: '', email: '', dob: '', gender: '', username: '', password: '', confirmPwd: '' });
                setResult(null); setErr('');
              }}
                style={{ background: 'none', border: 'none', color: '#9aa0a6', fontSize: 13, cursor: 'pointer' }}>
                Register another patient
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.4)',
        textAlign: 'center', padding: '8px 16px', fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
        Powered by SmaBizZ · smabizz.com
      </div>
    </div>
  );
}
